import { StateCreator } from 'zustand';

interface ITableConfig {
  grid: string;
  hiddenColumns: number[];
  columnsNumber?: number;
  minimumColumns?: number;
}

interface ITableState {
  tablesConfig: Record<string, ITableConfig>;
}

interface ITableActions {
  changeTableConfig: (id: string, config: Partial<ITableConfig>) => void;
  toggleTableColumn: (tableId: string, column: number) => void;
  isTableColumnHidden: (tableId: string, column: number) => boolean;
  isTableColumnDisabled: (tableId: string, column: number) => boolean;
}

export interface ITableStore extends ITableState, ITableActions {}

export const createTableSlice: StateCreator<ITableStore> = (set, get) => ({
  tablesConfig: {} as Record<string, ITableConfig>,
  changeTableConfig: (id: string, config: Partial<ITableConfig>) => {
    set(state => ({
      tablesConfig: {
        ...state.tablesConfig,
        [id]: {
          grid: config.grid ?? state.tablesConfig[id]?.grid ?? '',
          hiddenColumns:
            config.hiddenColumns ?? state.tablesConfig[id]?.hiddenColumns ?? [],
          columnsNumber:
            config.columnsNumber ?? state.tablesConfig[id]?.columnsNumber,
          minimumColumns:
            config.minimumColumns ??
            state.tablesConfig[id]?.minimumColumns ??
            2,
        },
      },
    }));
  },
  toggleTableColumn: (tableId: string, column: number) => {
    set(state => {
      const tableConfig = state.tablesConfig[tableId];
      if (!tableConfig) return state;

      const isHidden = tableConfig.hiddenColumns.includes(column);
      const columnsNumber = tableConfig.columnsNumber ?? 0;
      const minimumColumns = tableConfig.minimumColumns ?? 2;

      if (
        !isHidden &&
        columnsNumber - tableConfig.hiddenColumns.length <= minimumColumns
      ) {
        return state;
      }

      const updatedHiddenColumns = isHidden
        ? tableConfig.hiddenColumns.filter(c => c !== column)
        : [...tableConfig.hiddenColumns, column];

      return {
        tablesConfig: {
          ...state.tablesConfig,
          [tableId]: {
            ...tableConfig,
            hiddenColumns: updatedHiddenColumns,
          },
        },
      };
    });
  },
  isTableColumnHidden: (tableId: string, column: number) => {
    const tableConfig = get().tablesConfig[tableId];
    return tableConfig?.hiddenColumns.includes(column) ?? false;
  },
  isTableColumnDisabled: (tableId: string, column: number) => {
    const tableConfig = get().tablesConfig[tableId];
    if (!tableConfig) return false;

    const columnsNumber = tableConfig.columnsNumber ?? 0;
    const minimumColumns = tableConfig.minimumColumns ?? 2;
    return (
      columnsNumber - tableConfig.hiddenColumns.length <= minimumColumns &&
      !tableConfig.hiddenColumns.includes(column)
    );
  },
});
